/**
 * Notifications API
 *
 * Handles all notification-related API calls.
 * Uses Zod validation for response types.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  notificationsListResponseSchema,
  notificationCountResponseSchema,
} from '@aica/api-types/schemas';
import type {
  NotificationsListResponse,
  NotificationCountResponse,
  SuccessResponse,
} from '../types';

export class NotificationsApi extends BaseApiClient {
  /**
   * Get recent notifications (5 unread for dropdown) - validated with Zod
   */
  async getRecentNotifications(): Promise<NotificationsListResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.NOTIFICATIONS_RECENT,
      notificationsListResponseSchema
    );
  }

  /**
   * Get notifications with pagination and filters - validated with Zod
   */
  async getNotifications(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    category?: string;
  }): Promise<NotificationsListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.category) searchParams.set('category', params.category);

    const qs = searchParams.toString();
    const url = `${API_ENDPOINTS.NOTIFICATIONS}${qs ? `?${qs}` : ''}`;
    return this.validatedRequest(url, notificationsListResponseSchema);
  }

  async markNotificationRead(id: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.NOTIFICATIONS_READ(id), {
      method: 'PATCH',
    });
  }

  /**
   * Get notification count - validated with Zod
   */
  async getNotificationCount(): Promise<NotificationCountResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.NOTIFICATIONS_COUNT,
      notificationCountResponseSchema
    );
  }

  async markAllNotificationsRead(): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.NOTIFICATIONS_READ_ALL, {
      method: 'PATCH',
    });
  }
}
